'use client';

import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';

import { sceneItem, stagger } from '@/lib/motion';

import './product-page.css';

/**
 * Ürün sayfalarının (/features/*) beyaz/gri bölüm iskeleti ve başlık bloğu.
 *
 * Kap anasayfanın bölümleriyle aynı ızgarada: `.grid-item-8fr` içinde dikey
 * saç teli hatlı bir çerçeve (`.ps-inner`), yatay dolgu 26px → 30px (579px).
 * Bu hiza bozulursa hero'dan footer'a inen dikey çizgi kırılır.
 *
 * Zemin ritmi: koyu blok bitince beyaz ve gri (`--gray-50`) bölümler
 * dönüşümlü — sayfa `tone` ile seçiyor, iki beyaz bölüm yan yana gelince tek
 * uzun gövde gibi akıyordu.
 *
 * Hareket: bölümler sayfa açılır açılmaz son hâlinde (kullanıcı kararı,
 * 9 Eyl 2026 — kaydırmayla açılan giriş yok). Variant'lar `initial={false}`
 * ile duruyor; bölümün asıl hareketi kendi gösteriminde.
 */

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

export function ProductSection({
  tone = 'white',
  labelledBy,
  className,
  children,
}: {
  tone?: 'white' | 'gray';
  labelledBy: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={`ps ps--${tone}${className ? ` ${className}` : ''}`}
      aria-labelledby={labelledBy}
    >
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="ps-inner">{children}</div>
        </div>
      </div>
    </section>
  );
}

/**
 * Bölüm başlığı: etiket · başlık · giriş cümlesi. `title` zengin metin
 * (`t.rich(…, { hl })`) — vurgulu parça marka yeşilinde.
 */
export function SectionHead({
  id,
  eyebrow,
  title,
  lead,
  wide = false,
}: {
  id: string;
  eyebrow: string;
  title: ReactNode;
  lead?: ReactNode;
  /** Giriş cümlesi geniş mi (iki sütunlu gösterimin üstünde). */
  wide?: boolean;
}) {
  return (
    <motion.div
      className={`ps-head${wide ? ' ps-head--wide' : ''}`}
      variants={headV}
      initial={false}
      animate="show"
    >
      <motion.p className="ps-eyebrow" variants={sceneItem}>
        {eyebrow}
      </motion.p>
      <motion.h2 className="ps-title" id={id} variants={sceneItem}>
        {title}
      </motion.h2>
      {lead ? (
        <motion.p className="ps-lead" variants={sceneItem}>
          {lead}
        </motion.p>
      ) : null}
    </motion.div>
  );
}
